/**
 * Offline Cache Manager using IndexedDB
 * Provides offline storage and sync queue for eventual consistency
 */

const DB_NAME = 'mythkit_offline';
const DB_VERSION = 2; // Increased to match existing database version

interface SyncQueueItem {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: string;
  entityId: string;
  data: any;
  timestamp: number;
  retries: number;
}

class OfflineCache {
  private db: IDBDatabase | null = null;
  private syncQueue: SyncQueueItem[] = [];

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        // Log error but don't fail completely - app can work without IndexedDB
        // Continue without IndexedDB - operations will fall back to localStorage
        resolve();
      };
      request.onsuccess = () => {
        this.db = request.result;
        // Verify all object stores exist, recreate if needed
        this.ensureObjectStores().then(() => resolve()).catch(reject);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.createObjectStores(db);
      };
    });
  }

  private createObjectStores(db: IDBDatabase): void {
    // Sync queue store
    if (!db.objectStoreNames.contains('syncQueue')) {
      const queueStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
      queueStore.createIndex('timestamp', 'timestamp', { unique: false });
      queueStore.createIndex('entity', 'entity', { unique: false });
    }

    // Cache stores for different entity types
    const cacheStores = ['characters', 'campaigns', 'combats', 'encounters', 'combatParticipants'];
    cacheStores.forEach(storeName => {
      if (!db.objectStoreNames.contains(storeName)) {
        const store = db.createObjectStore(storeName, { keyPath: 'id' });
        store.createIndex('updated_at', 'updated_at', { unique: false });
      }
    });
  }

  private async ensureObjectStores(): Promise<void> {
    if (!this.db) return;
    // Just verify stores exist - if they don't, operations will gracefully fail
    // and fall back to localStorage. No need to trigger upgrade here.
  }

  private hasObjectStore(storeName: string): boolean {
    return this.db !== null && this.db.objectStoreNames.contains(storeName);
  }

  // Cache operations
  async cacheEntity(storeName: string, entity: any): Promise<void> {
    if (!this.db) {
      try {
        await this.init();
      } catch (error) {
        // IndexedDB not available, silently fail
        return;
      }
    }
    
    if (!this.hasObjectStore(storeName)) {
      // Store doesn't exist, silently fail (fallback to localStorage)
      return;
    }

    return new Promise((resolve) => {
      if (!this.db) {
        resolve();
        return;
      }

      try {
        const transaction = this.db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put({
          ...entity,
          cached_at: Date.now(),
        });

        request.onsuccess = () => resolve();
        request.onerror = () => {
          // Silently fail - localStorage will be used instead
          resolve();
        };
      } catch (error) {
        // Silently fail
        resolve();
      }
    });
  }

  async getCachedEntity(storeName: string, id: string): Promise<any | null> {
    if (!this.db) {
      try {
        await this.init();
      } catch (error) {
        // IndexedDB not available, return null
        return null;
      }
    }
    
    if (!this.hasObjectStore(storeName)) {
      // Store doesn't exist, return null (fallback to localStorage)
      return null;
    }

    return new Promise((resolve) => {
      if (!this.db) {
        resolve(null);
        return;
      }

      try {
        const transaction = this.db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(id);

        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => {
          // Silently fail - return null, localStorage will be used instead
          resolve(null);
        };
      } catch (error) {
        // Silently fail
        resolve(null);
      }
    });
  }

  async getAllCached(storeName: string): Promise<any[]> {
    if (!this.db) {
      try {
        await this.init();
      } catch (error) {
        // IndexedDB not available, return empty array
        return [];
      }
    }
    
    if (!this.hasObjectStore(storeName)) {
      // Store doesn't exist, return empty array (fallback to localStorage)
      return [];
    }

    return new Promise((resolve) => {
      if (!this.db) {
        resolve([]);
        return;
      }

      try {
        const transaction = this.db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => {
          // Silently fail - return empty array, localStorage will be used instead
          resolve([]);
        };
      } catch (error) {
        // Silently fail
        resolve([]);
      }
    });
  }

  async clearCache(storeName: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /** Remove one cached row by primary key (string or number id). */
  async deleteCachedEntity(storeName: string, id: string | number): Promise<void> {
    if (!this.db) {
      try {
        await this.init();
      } catch {
        return;
      }
    }
    if (!this.hasObjectStore(storeName) || !this.db) return;
    const keys = new Set<string>([String(id)]);
    const n = typeof id === 'number' ? id : parseInt(String(id), 10);
    if (Number.isFinite(n)) keys.add(String(n));

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        for (const k of keys) {
          store.delete(k);
        }
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  // Sync queue operations
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    if (!this.db) await this.init();

    const queueItem: SyncQueueItem = {
      id: `${Date.now()}-${Math.random()}`,
      ...item,
      timestamp: Date.now(),
      retries: 0,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      const request = store.add(queueItem);

      request.onsuccess = () => {
        this.syncQueue.push(queueItem);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readonly');
      const store = transaction.objectStore('syncQueue');
      const request = store.getAll();

      request.onsuccess = () => {
        this.syncQueue = request.result || [];
        resolve(this.syncQueue);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async removeFromSyncQueue(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      const request = store.delete(id);

      request.onsuccess = () => {
        this.syncQueue = this.syncQueue.filter(item => item.id !== id);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async incrementRetry(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (item) {
          item.retries += 1;
          const putRequest = store.put(item);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Check if online
  isOnline(): boolean {
    return navigator.onLine;
  }

  // Sync queue when online (to be called periodically or on online event)
  async processSyncQueue(apiClient: any): Promise<{ success: number; failed: number }> {
    if (!this.isOnline()) {
      return { success: 0, failed: 0 };
    }

    const queue = await this.getSyncQueue();
    let success = 0;
    let failed = 0;

    for (const item of queue) {
      try {
        // Max retries
        if (item.retries >= 5) {
          await this.removeFromSyncQueue(item.id);
          failed++;
          continue;
        }

        // Execute sync based on type
        let result;
        switch (item.type) {
          case 'create':
            result = await apiClient.post(`/api/${item.entity}`, item.data);
            break;
          case 'update':
            result = await apiClient.put(`/api/${item.entity}/${item.entityId}`, item.data);
            break;
          case 'delete':
            result = await apiClient.delete(`/api/${item.entity}/${item.entityId}`);
            break;
        }

        // Success - remove from queue
        await this.removeFromSyncQueue(item.id);
        success++;
      } catch (error) {
        // Increment retry count
        await this.incrementRetry(item.id);
        failed++;
      }
    }

    return { success, failed };
  }
}

export const offlineCache = new OfflineCache();

// Initialize on module load
if (typeof window !== 'undefined') {
  offlineCache.init().catch(() => {});
}
